// ADR 5.9.26u §3 — the top-level destinations, in one place. ADR 28.9.26e §1
// made them Android's.
//
// The bottom bar, the rail and the sidebar are three drawings of one list, so
// the list lives here rather than three times. Every label is an existing
// catalogue key, and every entry is Android's own (`TopLevelTab.kt`): Home ·
// Vocab · Flashcards · Lessons, with Android's glyphs. `/plans` keeps its URL
// — share links, `_redirects` and the two-pane matcher spell it — and only the
// label becomes `tab_lessons`.
//
// A constant, not a hook. The Tutor used to be a destination gated on
// 5.9.26s's entitlement fold, and the Account a fifth item; since 28.9.26e the
// tutor is reached by the FAB (Android's two routes) and the account by the
// header's gear, so the list no longer depends on the session: signed in and
// signed out see the same four.
//
// `Destination[]`, not `readonly`: five consumers take `items: Destination[]`,
// and widening five signatures to protect a constant nobody writes is not worth it.
//
// **Home's active state.** `NavLink to="/"` is active only at `/` — React
// Router's match requires a `/` after the prefix, and `/` has none — so its
// pill does not light on every route (28.9.26e AC1 pins it).
//
// The icons are decorative — `Icon` renders them `aria-hidden` by default:
// the accessible name is the translated label, never the symbol (15.9.26ag).

//
// ADR 30.9.26s §1 — **two consoles, two lists.** `/admin` draws its own
// five destinations in place of these four, chosen by path (`destinationsFor`),
// never by size class, so the three drawings stay ignorant of which list they
// were handed. Still a constant module, not a hook: until O5 the flag that
// admits a user to the console is the flag that shows its clients section.
// Since O5 (Backend 25.9.26n §12) a live grant admits too, and Connected apps
// is the fifth destination (8.10.26b §1 put the builder fourth).

//
// ADR 30.9.26y §1 — Webhooks shipped, but its route 404s while
// `enable_webhooks` is dark (roadmap D10), so its `soon` stopped being a
// literal and became a function of the flags (`readyWhen`): a live link to a
// not-found page would be a broken door. `destinationsFor` is still pure — of
// the path *and the flags* — and the drawings still only read `soon`.

import type { FeatureFlags } from '../../api/auth'
import { appsAdmit, builderAdmits, webhooksAdmit } from '../../features/admin/sections'
import type { IconName } from '../../ui/icons'

/** One top-level destination: where it goes, what it is called, what it looks like. */
export interface Destination {
  path: string
  /** A key in the generated catalogue (`ios/locales`, 5.9.26i §3). */
  labelKey: string
  /** Decorative only — the label is the accessible name. */
  icon: IconName
  /**
   * Current only at `path` exactly, never at a sub-path (NavLink's `end`).
   * Per destination rather than on every link: Lessons must stay current on
   * `/plans/:id`, while OAuth clients must not light up on `/admin/webhooks`.
   */
  end?: true
  /** Reserved, not yet shipped: drawn disabled with a "Soon" badge, never navigable. */
  soon?: true
  /** ADR 30.9.26y §1 — drawn as `soon` until this holds for the viewer's flags; absent means always ready. */
  readyWhen?: (flags: FeatureFlags) => boolean
}

/** Android's four tabs, in Android's order. */
export const DESTINATIONS: Destination[] = [
  { path: '/', labelKey: 'tab_home', icon: 'home' },
  { path: '/vocab', labelKey: 'tab_vocab', icon: 'list' },
  { path: '/flashcards', labelKey: 'tab_flashcards', icon: 'star' },
  { path: '/plans', labelKey: 'tab_lessons', icon: 'import_contacts' },
]

/**
 * ADR 30.9.26s §1 — the integrations console's destinations.
 *
 * OAuth clients is `/admin` itself, so every existing link to the console
 * keeps working. Webhooks is a route (30.9.26y), and so is App configuration
 * (30.9.26ai, which moved the reserved `/admin/app-config` slug to
 * `/admin/apps`); each is `soon` until its flag is on.
 *
 * Backend 25.9.26n §12 — Connected apps is always ready inside the console
 * (a grant-only learner's landing, and a developer's own grants), and OAuth
 * clients is `soon` without `enable_oauth_clients`, so a grant-only user never
 * sees it lit. `readyWhen` keeps its flags-only signature: the shell does not
 * fetch grants on every page to draw a rail.
 */
export const ADMIN_DESTINATIONS: Destination[] = [
  {
    path: '/admin',
    labelKey: 'admin_nav_clients',
    icon: 'vpn_key',
    end: true,
    readyWhen: (flags) => flags.enable_oauth_clients === true,
  },
  // `hasLiveGrant: false` is exact, not a guess: neither predicate reads the fact (Backend 25.9.26n §12).
  { path: '/admin/webhooks', labelKey: 'admin_nav_webhooks', icon: 'webhook', readyWhen: (flags) => webhooksAdmit({ flags, hasLiveGrant: false }) },
  { path: '/admin/apps', labelKey: 'admin_nav_app_config', icon: 'tune', readyWhen: (flags) => appsAdmit({ flags, hasLiveGrant: false }) },
  // 8.10.26b §1 — the builder lives at /connections/build (2.10.26g §2), not under /admin;
  // §2 makes its routes the console's. Fourth, not last: Connected apps stays a grant-only learner's landing.
  {
    path: '/connections/build',
    labelKey: 'admin_nav_builder',
    icon: 'build',
    readyWhen: (flags) => builderAdmits({ flags, hasLiveGrant: false }),
  },
  { path: '/admin/connected-apps', labelKey: 'admin_nav_connected_apps', icon: 'link' },
]

/** Which of the two consoles a path belongs to. */
export type Console = 'app' | 'admin'

function under(pathname: string, root: string): boolean {
  return pathname === root || pathname.startsWith(`${root}/`)
}

/**
 * The console is `/admin` and `/connections/build` and everything below each, by segment:
 * `/administrator`, `/admins`, `/connections`, `/connections/builder` and `/connections/buildx`
 * are the learning app's.
 */
export function consoleFor(pathname: string): Console {
  return under(pathname, '/admin') || under(pathname, '/connections/build') ? 'admin' : 'app'
}

/**
 * The list the rail, the sidebar, the bar and the header's fallback draw. A
 * destination with `readyWhen` is `soon` until the flags (null while loading)
 * satisfy it; the header's title fallback omits them, since a title reads no `soon`.
 */
export function destinationsFor(pathname: string, flags: FeatureFlags | null = null): Destination[] {
  if (consoleFor(pathname) === 'app') return DESTINATIONS
  return ADMIN_DESTINATIONS.map((item) =>
    item.readyWhen === undefined || (flags !== null && item.readyWhen(flags)) ? item : { ...item, soon: true },
  )
}
